import { Clock, Lock } from "lucide-react";
import { useEffect, useState } from "react";

import { useLanguage } from "@/contexts/language";
import { cn } from "@/lib/utils";

import { Num, windowRemaining, windowState, type WindowState } from "./lib";

// Ported from the reference session-pill.tsx, simplified to take the window
// fields directly and re-rendering every 30s for a live countdown.

const STATE_STYLE: Record<WindowState, string> = {
  open: "tw:bg-success-soft tw:text-success-foreground",
  closing: "tw:bg-warning-soft tw:text-warning",
  closed: "tw:bg-destructive-soft tw:text-destructive",
};

function useNow(intervalMs: number) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((n) => n + 1), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
}

/**
 * Minimal window countdown badge overlapping the avatar in the thread's top
 * bar: same colors as the desktop pill, but digits only ("23:12" — a bare
 * lock once closed).
 */
export function SessionCountdown({
  windowOpen,
  windowExpiresAt,
  plain,
}: {
  windowOpen: boolean;
  windowExpiresAt: string | null;
  /** No state colors — inherits the surrounding surface (composer tab). */
  plain?: boolean;
}) {
  useNow(30_000);

  const state = windowState(windowOpen, windowExpiresAt);
  const remaining = windowRemaining(windowExpiresAt);
  const Icon = state === "closed" ? Lock : Clock;

  return (
    <span
      className={cn(
        "tw:inline-flex tw:items-center tw:rounded-full tw:font-semibold",
        // Plain lives in the composer banner beside the action chips — match
        // their type scale; colored is the tiny badge overlapping the avatar.
        plain
          ? "tw:gap-1 tw:py-1 tw:text-xs tw:leading-[1.5] tw:text-muted-foreground"
          : cn(
              "tw:gap-0.5 tw:px-1.5 tw:py-px tw:text-[10px] tw:leading-[1.4]",
              STATE_STYLE[state],
            ),
      )}
    >
      <Icon
        className={cn("tw:shrink-0", plain ? "tw:size-3.5" : "tw:size-2.5")}
        strokeWidth={2.5}
      />
      {state !== "closed" && remaining ? (
        <span dir="ltr" className="tw:whitespace-nowrap tw:tabular-nums">
          <Num>
            {remaining.hours}:{String(remaining.minutes).padStart(2, "0")}
          </Num>
        </span>
      ) : null}
    </span>
  );
}

export function SessionPill({
  windowOpen,
  windowExpiresAt,
  compact,
}: {
  windowOpen: boolean;
  windowExpiresAt: string | null;
  compact?: boolean;
}) {
  const { t } = useLanguage();
  useNow(30_000);

  const state = windowState(windowOpen, windowExpiresAt);
  const remaining = windowRemaining(windowExpiresAt);
  const Icon = state === "closed" ? Lock : Clock;
  const label =
    state === "open"
      ? t("Mandoob:Window.Open")
      : state === "closing"
        ? t("Mandoob:Window.Closing")
        : t("Mandoob:Window.Closed");

  return (
    <span
      className={cn(
        "tw:inline-flex tw:items-center tw:gap-1.5 tw:rounded-full tw:px-2.5 tw:py-0.5 tw:text-xs tw:font-medium tw:leading-[1.67]",
        STATE_STYLE[state],
      )}
    >
      <Icon className="tw:size-3.5" strokeWidth={2} />
      {!compact && label}
      {state !== "closed" && remaining ? (
        compact ? (
          <span className="tw:tabular-nums">
            <Num>{remaining.hours}</Num>
            {t("Mandoob:Window.HourShort")} <Num>{remaining.minutes}</Num>
            {t("Mandoob:Window.MinuteShort")}
          </span>
        ) : (
          <>
            <span className="tw:opacity-50">•</span>
            <span className="tw:tabular-nums">
              <Num>{remaining.hours}</Num>
              {t("Mandoob:Window.HourShort")} <Num>{remaining.minutes}</Num>
              {t("Mandoob:Window.MinuteShort")}
            </span>
          </>
        )
      ) : null}
    </span>
  );
}
